import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Slide, ToastContainer } from 'react-toastify';

import { localeToDir } from '@/utils/seo';

import { AUTO_CLOSE_MS, notify } from './notify';

/**
 * The site's single toast container, mounted as a direct child of <body>
 * instead of inside the app tree.
 *
 * Headless UI's Dialog takes the `body > *` element that contains it and sets
 * `inert` on it (useInert in @headlessui/react) so the page behind a modal
 * cannot be reached; the dialog itself escapes because it renders into
 * #headlessui-portal-root. A container rendered inside the app tree is inerted
 * along with everything else, which kills the toast's close button while any
 * modal is open — and the auth modals raise their errors as toasts.
 *
 * Being a sibling of the inerted branch also keeps the modal from closing when
 * the toast is clicked: Dialog's useRootContainers treats every `body > *` that
 * does not contain the dialog as one of its own containers, so those clicks do
 * not count as outside clicks.
 *
 * Living outside the app tree is also why the toasts need sweeping by hand: a
 * route change unmounts the page that raised them but not this container, so
 * without the effect below a message about the screen someone just left stayed
 * on screen over the next one. `notify` decides what the sweep may take — a
 * toast raised right before a redirect opts out, since its message is about
 * where the person is going, not where they were.
 *
 * The visible box is the Alert component (rendered by notify()), so the
 * library's icon, close button and progress bar are turned off here and the
 * duration comes from notify. `limit` caps the stack so a burst of
 * gateway errors cannot bury the page.
 */
export function ToastHost() {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const router = useRouter();

  useEffect(() => {
    setHost(document.body);
  }, []);

  useEffect(() => {
    // `events` is absent when this renders outside a router context (a test
    // rendering ToastHost on its own), and there is nothing to sweep there.
    const events = router?.events;
    if (!events) return undefined;

    const dismissOnNavigation = () => notify.dismissPageScoped();

    events.on('routeChangeStart', dismissOnNavigation);
    return () => events.off('routeChangeStart', dismissOnNavigation);
  }, [router?.events]);

  if (!host) return null;

  // The portal lives outside the tree that inherits `dir` from <html>, so the
  // direction has to be handed to the container explicitly; the position is
  // mirrored along with it.
  const isRtl = localeToDir(router?.locale ?? 'en') === 'rtl';

  return createPortal(<ToastContainer position={isRtl ? 'top-left' : 'top-right'} transition={Slide} limit={4} autoClose={AUTO_CLOSE_MS} hideProgressBar closeButton={false} icon={false} newestOnTop closeOnClick rtl={isRtl} pauseOnFocusLoss draggable pauseOnHover theme='dark' />, host);
}
